import axios from "axios";
import React, {
  useState,
  useEffect,
} from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import {
  createOrder,
} from "../../actions/orderActions";
import {
  useCart,
} from "../../components/CartContext";
import {
  useRestaurantSession,
} from "../../components/RestaurantSessionProvider";
import {
  isSessionExpiredError,
  getSessionExpiredMessage,
} from "../../utils/sessionErrorHandler";
import { fetchPriceSummary } from "../../actions/priceActions";
import "./css/OrderScreen.css";
/*
\=========================================================
IMAGE URL HELPER
\=========================================================
*/
function getImageUrl(image) {
  if (!image) {
    return "";
  }
  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }
  return `${image}`;
}
/*
\=========================================================
ORDER SCREEN
\=========================================================
*/
function OrderScreen() {
  const {
    restaurantId,
    tableId,
  } = useParams();
  const navigate =
    useNavigate();
  const dispatch =
    useDispatch();
  const [paymentOptions, setPaymentOptions] = useState(null);
  useEffect(() => {
    let active = true;
    setPaymentOptions(null);
    axios.get(`/api/restaurants/${encodeURIComponent(restaurantId)}/payment-options/`)
      .then(({data}) => { if (active) setPaymentOptions(data); })
      .catch(() => { if (active) setPaymentOptions({allow_cash_payment:false,allow_online_payment:false}); });
    return () => { active = false; };
  }, [restaurantId]);
  const onlineAllowed = paymentOptions?.allow_online_payment === true;
  const cashAllowed = paymentOptions?.allow_cash_payment === true;

  /*
  \=======================================================
  DINING SESSION
  \=======================================================
  */
  const {
    sessionToken,
  } = useRestaurantSession();
  /*
  \=======================================================
  RESTAURANT
  \=======================================================
  */
  const themeState =
    useSelector(
      (state) =>
        state.theme || {}
    );
  const restaurantName =
    themeState.restaurantName ||
    themeState.restaurant_name ||
    "INDIAN CUISINE";
  /*
  \=======================================================
  CART
  \=======================================================
  */
  const {
    cart,
    cartCount,
    clearCart,
  } = useCart();
  /*
  \=======================================================
  PRICE DATA FROM CHECKOUT
  \=======================================================
  */
  const { loading: priceLoading, summary: priceSummary, error: priceError } =
    useSelector((state) => state.price || {});

  useEffect(() => {
    if (!cart || cart.length === 0) return;
    const items = cart.map((item) => ({
      menu_item_id: item.id,
      quantity: Number(item.quantity),
    }));
    dispatch(fetchPriceSummary(restaurantId, items));
  }, [dispatch, restaurantId, cart]);

  const priceReady = !priceLoading && !priceError && !!priceSummary &&
    priceSummary.total != null && Number.isFinite(Number(priceSummary.total));
  const subtotal = Number(priceSummary?.subtotal || 0);
  const tax = Number(priceSummary?.tax || 0);
  const serviceCharge = Number(priceSummary?.service_charge || 0);
  const discount = Number(priceSummary?.discount || 0);
  const total = Number(priceSummary?.total || 0);
  /*
  \=======================================================
  PAYMENT METHOD
  \=======================================================
  */
  const [
    paymentMethod,
    setPaymentMethod,
  ] = useState("CASH");
  /*
  \=======================================================
  NOTES
  \=======================================================
  */
  const [
    notes,
    setNotes,
  ] = useState("");
  /*
  \=======================================================
  LOADING / ERROR
  \=======================================================
  */
  const [
    loading,
    setLoading,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState("");
  /*
  \=======================================================
  ORDER REDUX STATE
  \=======================================================
  */
  const orderState =
    useSelector(
      (state) =>
        state.order || {}
    );
  const reduxError =
    orderState.error;
  /*
  \=======================================================
  BACK TO CHECKOUT
  \=======================================================
  */
  const handleBack = () => {
    navigate(
      `/${restaurantId}/table/${tableId}/checkout`
    );
  };
  /*
  \=======================================================
  CREATE ORDER
  \=======================================================
  */
  const handleCreateOrder =
    async () => {
      if (loading) return;
      if (!paymentOptions || (paymentMethod === "ONLINE" && !onlineAllowed) || (paymentMethod === "CASH" && !cashAllowed) || !paymentMethod) { setError("Selected payment method is unavailable."); return; }
      if (!priceReady) {
        setError("Final price is not ready. Please wait or try again.");
        return;
      }
      /*
      \-----------------------------------------------------
      CHECK CART
      \-----------------------------------------------------
      */
      if (
        !cart ||
        cart.length === 0
      ) {
        setError(
          "Your cart is empty."
        );
        return;
      }
      /*
      \-----------------------------------------------------
      CHECK DINING SESSION
      \-----------------------------------------------------
      */
      if (!sessionToken) {
        setError(
          "Dining session is not ready. Please try again."
        );
        return;
      }
      try {
        setLoading(true);
        setError("");
        /*
        \---------------------------------------------------
        ORDER PAYLOAD
        \---------------------------------------------------
        */
        const orderData = {
          items:
            cart.map(
              (item) => ({
                menu_item_id:
                  item.id,
                quantity:
                  Number(
                    item.quantity
                  ),
              })
            ),
          /*
          \-------------------------------------------------
          DINING SESSION
          \-------------------------------------------------
          */
          session_token:
            sessionToken,
          payment_method:
            paymentMethod,
          table_id:
            Number(tableId),
          notes:
            notes.trim(),
        };
        /*
        \---------------------------------------------------
        CREATE ORDER
        \---------------------------------------------------
        */
        const response =
          await dispatch(
            createOrder(
              restaurantId,
              orderData
            )
          );
        /*
        \---------------------------------------------------
        GET CREATED ORDER
        \---------------------------------------------------
        */
        const createdOrder =
          response?.order ||
          response?.data?.order ||
          response?.data ||
          response;
        const orderId =
          createdOrder?.id ||
          createdOrder?.order_id ||
          response?.order_id;
        if (!orderId) throw new Error("Order ID was not returned by the server.");
        const confirmedTotal = Number(createdOrder?.total ?? total);
        /*
        \---------------------------------------------------
        CASH PAYMENT
        \---------------------------------------------------
        */
        if (
          paymentMethod === "CASH"
        ) {
          clearCart();
          navigate(
            `/${restaurantId}/table/${tableId}/success`,
            {
              state: {
                orderId:
                  orderId,
                paymentMethod:
                  "CASH",
                total:
                  confirmedTotal,
              },
            }
          );
          return;
        }
        /*
        \---------------------------------------------------
        ONLINE PAYMENT
        \---------------------------------------------------
        Pass the COMPLETE price summary.
        This allows PaymentScreen to go back
        to OrderScreen without losing the totals.
        \---------------------------------------------------
        */
        navigate(
          `/${restaurantId}/table/${tableId}/payment`,
          {
            state: {
              orderId:
                orderId,
              subtotal:
                subtotal,
              tax:
                tax,
              serviceCharge:
                serviceCharge,
              discount,
              total:
                confirmedTotal,
            },
          }
        );
      } catch (err) {
        console.error(
          "Create order error:",
          err
        );
        /*
        \=================================================
        SESSION EXPIRED
        \=================================================
        */
        if (
          isSessionExpiredError(err)
        ) {
          setError(
            "Your table session has ended. " +
            "Please scan the table QR code again " +
            "to start a new session."
          );
          return;
        }
        /*
        \=================================================
        OTHER ERRORS
        \=================================================
        */
        setError(
          getSessionExpiredMessage(err)
        );
      } finally {
        setLoading(false);
      }
    };
  /*
  \=======================================================
  EMPTY CART
  \=======================================================
  */
  if (
    !cart ||
    cart.length === 0
  ) {
    return (
      <main className="theme5-order-page">
        <div className="theme5-order-empty">
          <span>
            YOUR ORDER
          </span>
          <h1>
            YOUR CART
            <br />
            IS EMPTY.
          </h1>
          <p>
            Please add something from
            our Indian menu before continuing.
          </p>
          <button
            type="button"
            onClick={() =>
              navigate(
                `/${restaurantId}/table/${tableId}`
              )
            }
          >
            VIEW MENU
            <span>
              →
            </span>
          </button>
        </div>
      </main>
    );
  }
  /*
  \=======================================================
  PAGE
  \=======================================================
  */
  return (
    <main className="theme5-order-page">
      {/* =================================================
          BLUE HEADER
      \================================================= */}
      <section className="theme5-order-hero">
        <div className="theme5-order-container">
          <button
            type="button"
            className="theme5-order-back"
            onClick={handleBack}
          >
            <span>
              ←
            </span>
            BACK TO CHECKOUT
          </button>
          <div className="theme5-order-heading">
            <span>
              {restaurantName}
            </span>
            <h1>
              YOUR ORDER
            </h1>
            <p>
              TABLE {tableId}
              {" • "}
              {cartCount}
              {" "}
              {cartCount === 1
                ? "ITEM"
                : "ITEMS"
              }
            </p>
          </div>
        </div>
      </section>
      {/* =================================================
          MAIN
      \================================================= */}
      <section className="theme5-order-content">
        <div className="theme5-order-container">
          <div className="theme5-order-layout">
            {/* =============================================
                LEFT SIDE
            \============================================= */}
            <div className="theme5-order-main">
              {/* ===========================================
                  PAYMENT METHOD
              \=========================================== */}
              <section className="theme5-order-section">
                <div className="theme5-order-section-heading">
                  <span>
                    01
                  </span>
                  <h2>
                    PAYMENT
                  </h2>
                </div>
                <div className="theme5-payment-options">
                  {/* CASH */}
                  <button
                    type="button"
                    className={
                      `theme5-payment-option ${
                        cashAllowed && paymentMethod === "CASH"
                          ? "theme5-payment-option-active"
                          : ""
                      }`
                    }
                    disabled={!cashAllowed}
                    style={!cashAllowed ? {opacity:0.4,cursor:"not-allowed"} : undefined}
                    onClick={() => cashAllowed && setPaymentMethod("CASH")}
                  >
                    <span className="theme5-payment-radio">
                      {paymentMethod ===
                        "CASH" && (
                        <span />
                      )}
                    </span>
                    <span className="theme5-payment-icon">
                      💵
                    </span>
                    <span className="theme5-payment-content">
                      <strong>
                        CASH
                      </strong>
                      <small>
                        Pay by cash at the restaurant.
                      </small>
                    </span>
                    {paymentMethod ===
                      "CASH" && (
                      <span className="theme5-payment-check">
                        ✓
                      </span>
                    )}
                  </button>
                  {/* ONLINE */}
                  <button
                    type="button"
                    className={
                      `theme5-payment-option ${
                        onlineAllowed && paymentMethod === "ONLINE"
                          ? "theme5-payment-option-active"
                          : ""
                      }`
                    }
                    disabled={!onlineAllowed}
                    style={!onlineAllowed ? {opacity:0.4,cursor:"not-allowed"} : undefined}
                    onClick={() => onlineAllowed && setPaymentMethod("ONLINE")}
                  >
                    <span className="theme5-payment-radio">
                      {paymentMethod ===
                        "ONLINE" && (
                        <span />
                      )}
                    </span>
                    <span className="theme5-payment-icon">
                      💳
                    </span>
                    <span className="theme5-payment-content">
                      <strong>
                        ONLINE PAYMENT
                      </strong>
                      <small>
                        {onlineAllowed ? "Secure payment with Razorpay." : "Online payment unavailable."}
                      </small>
                    </span>
                    {paymentMethod ===
                      "ONLINE" && (
                      <span className="theme5-payment-check">
                        ✓
                      </span>
                    )}
                  </button>
                </div>
              </section>
              {/* ===========================================
                  SPECIAL INSTRUCTIONS
              \=========================================== */}
              <section className="theme5-order-section">
                <div className="theme5-order-section-heading">
                  <span>
                    02
                  </span>
                  <h2>
                    SPECIAL INSTRUCTIONS
                  </h2>
                </div>
                <div className="theme5-order-notes-wrapper">
                  <textarea
                    className="theme5-order-notes"
                    value={notes}
                    onChange={(event) =>
                      setNotes(
                        event.target.value
                      )
                    }
                    placeholder="Any special instructions for the kitchen?"
                    rows="5"
                  />
                </div>
              </section>
              {/* ===========================================
                  ORDER ITEMS
              \=========================================== */}
              <section className="theme5-order-section">
                <div className="theme5-order-section-heading">
                  <span>
                    03
                  </span>
                  <h2>
                    ORDER ITEMS
                  </h2>
                </div>
                <div className="theme5-order-items">
                  {cart.map(
                    (
                      item,
                      index
                    ) => {
                      const imageUrl =
                        getImageUrl(
                          item.image ||
                          item.image_url ||
                          item.photo
                        );
                      return (
                        <article
                          className="theme5-order-item"
                          key={
                            item.id ||
                            `${item.name}-${index}`
                          }
                        >
                          <div className="theme5-order-item-image">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={
                                  item.name
                                }
                              />
                            ) : (
                              <div className="theme5-order-no-image">
                                INDIAN
                              </div>
                            )}
                          </div>
                          <div className="theme5-order-item-info">
                            <span>
                              {String(
                                index + 1
                              ).padStart(
                                2,
                                "0"
                              )}
                            </span>
                            <h3>
                              {item.name}
                            </h3>
                            <p>
                              Quantity:
                              {" "}
                              {item.quantity}
                            </p>
                          </div>
                          <strong>
                            ₹
                            {(
                              Number(
                                item.price || 0
                              ) *
                              Number(
                                item.quantity || 0
                              )
                            ).toFixed(2)}
                          </strong>
                        </article>
                      );
                    }
                  )}
                </div>
              </section>
            </div>
            {/* =============================================
                RIGHT SUMMARY
            \============================================= */}
            <aside className="theme5-order-summary">
              <span className="theme5-order-summary-label">
                TABLE {tableId}
              </span>
              <h2>
                READY
                <br />
                TO ORDER?
              </h2>
              <div className="theme5-order-summary-line" />
              <div className="theme5-order-summary-row">
                <span>
                  ITEMS
                </span>
                <strong>
                  {cartCount}
                </strong>
              </div>
              <div className="theme5-order-summary-row">
                <span>
                  SUBTOTAL
                </span>
                <strong>
                  ₹
                  {Number(
                    subtotal
                  ).toFixed(2)}
                </strong>
              </div>
              <div className="theme5-order-summary-row">
                <span>
                  TAX
                </span>
                <strong>
                  ₹
                  {Number(
                    tax
                  ).toFixed(2)}
                </strong>
              </div>
              <div className="theme5-order-summary-row">
                <span>
                  SERVICE CHARGE
                </span>
                <strong>
                  ₹
                  {Number(
                    serviceCharge
                  ).toFixed(2)}
                </strong>
              </div>
              {discount > 0 && (
                <div className="theme5-order-summary-row">
                  <span>DISCOUNT</span>
                  <strong>-₹{discount.toFixed(2)}</strong>
                </div>
              )}
              <div className="theme5-order-summary-divider" />
              <div className="theme5-order-total">
                <div>
                  <span>
                    TOTAL
                  </span>
                  <small>
                    INCLUDING ALL CHARGES
                  </small>
                </div>
                <strong>
                  ₹
                  {Number(
                    total
                  ).toFixed(2)}
                </strong>
              </div>
              {/* SELECTED PAYMENT */}
              <div className="theme5-selected-payment">
                <span>
                  PAYMENT
                </span>
                <strong>
                  {paymentMethod === "CASH"
                    ? "PAY AT TABLE"
                    : "ONLINE PAYMENT"
                  }
                </strong>
              </div>
              {/* ERROR */}
              {(error || reduxError || priceError) && (
                <div className="theme5-order-error">
                  {error || reduxError || (typeof priceError === "string" ? priceError : "Unable to calculate price.")}
                </div>
              )}
              {/* CREATE ORDER */}
              <button
                type="button"
                className="theme5-order-create-button"
                onClick={
                  handleCreateOrder
                }
                disabled={loading || !sessionToken || !priceReady}
              >
                {loading ? (
                  <>
                    <span className="theme5-order-spinner" />
                    CREATING ORDER...
                  </>
                ) : (
                  <>
                    CREATE ORDER
                    <span>
                      →
                    </span>
                  </>
                )}
              </button>
              <p className="theme5-order-security-text">
                {paymentMethod === "CASH"
                  ? "You will pay at the restaurant."
                  : "You will continue to secure online payment after the order is created."
                }
              </p>
            </aside>
          </div>
          {/* =================================================
              BOTTOM
          \================================================= */}
          <div className="theme5-order-bottom">
            <span>
              NAMASTE!
            </span>
            <p>
              Good food. Good friends.
              Good times.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
export default OrderScreen;